/**
 * L'état du tapis : trois dés identifiés, dont certains sont gardés.
 *
 * Module pur, sans React ni physique — ce qui permet de tester la seule partie
 * réellement piégeuse du lot 3 côté interface : **rendre les trajectoires aux bons
 * dés**.
 *
 * Quand on relance deux dés sur trois, le solveur rend DEUX trajectoires. Les
 * associer aux deux premiers dés du tapis paraît naturel et serait faux : les dés ont
 * une identité stable (docs/PLAN-V2.md § 3.2), et si l'association se décale, le dé
 * que le joueur croit garder change de valeur sous ses yeux. Le moteur, lui, n'en
 * saurait rien — c'est exactement le genre de bug qui ne lève aucune exception.
 */

import type { DePose, Jet } from "./lancer.js";
import { normale, type Face } from "./orientation.js";
import { Quaternion, Vector3 } from "three";

/**
 * Où les dés attendent avant le premier lancer, face 1 vers le haut.
 *
 * Au centre du plateau plutôt que sur un bord : le geste attaque désormais de
 * n'importe quel point du pourtour, et des dés posés d'un côté suggéreraient une
 * direction de lancer qui n'existe plus.
 */
const REPOS: ReadonlyArray<readonly [number, number, number]> = [
  [-1.1, 0, 0.6],
  [0, 0, 0.6],
  [1.1, 0, 0.6],
];

const ORIENTATION_REPOS: readonly [number, number, number, number] = (() => {
  const q = new Quaternion().setFromUnitVectors(normale(1), new Vector3(0, 1, 0));
  return [q.x, q.y, q.z, q.w];
})();

export interface DeSurTapis {
  /** Valeur affichée, ou `null` tant que le dé n'a jamais été lancé. */
  readonly face: Face | null;
  readonly garde: boolean;
  /** Position et orientation où le dé s'est arrêté. Sert de collider au jet suivant. */
  readonly pose: DePose;
}

export type EtatTapis = readonly [DeSurTapis, DeSurTapis, DeSurTapis];

export function tapisInitial(): EtatTapis {
  return REPOS.map((position) => ({
    face: null,
    garde: false,
    pose: { position, orientation: ORIENTATION_REPOS },
  })) as unknown as EtatTapis;
}

/**
 * Garde ou relâche un dé.
 *
 * Un dé jamais lancé ne peut pas être gardé : il n'a pas de valeur à garder, et
 * l'accepter laisserait le joueur bloquer la manche sur un dé neutre.
 */
export function basculerGarde(tapis: EtatTapis, index: number): EtatTapis {
  return tapis.map((de, i) => {
    if (i !== index || de.face === null) return de;
    return { ...de, garde: !de.garde };
  }) as unknown as EtatTapis;
}

/** Relâche tous les dés — début de manche, ou après validation d'un jet. */
export function toutRelacher(tapis: EtatTapis): EtatTapis {
  return tapis.map((de) => (de.garde ? { ...de, garde: false } : de)) as unknown as EtatTapis;
}

/** Index des dés à relancer, dans l'ordre du tapis. */
export function indicesARelancer(tapis: EtatTapis): readonly number[] {
  return tapis.flatMap((de, i) => (de.garde ? [] : [i]));
}

/** Poses des dés gardés : le jet suivant doit les contourner, pas les traverser. */
export function posesDesGardes(tapis: EtatTapis): readonly DePose[] {
  return tapis.filter((de) => de.garde).map((de) => de.pose);
}

/**
 * Faux quand les trois dés sont gardés. Garder les trois, c'est s'arrêter : le
 * moteur du 421 traite les deux gestes de la même façon, et l'interface ne doit pas
 * proposer un lancer vide.
 */
export function relanceUtile(tapis: EtatTapis): boolean {
  return indicesARelancer(tapis).length > 0;
}

/**
 * Où chaque dé s'est arrêté, à la fin de la trajectoire qui le concerne.
 *
 * `indices` doit être exactement la liste des dés relancés. On vérifie la longueur
 * plutôt que de tronquer en silence : une association décalée est invisible à l'œil
 * et fausserait tout le reste de la manche.
 */
function posesDuJet(
  indices: readonly number[], jet: Jet,
): ReadonlyMap<number, { readonly face: Face; readonly pose: DePose }> {
  if (indices.length !== jet.trajectoires.length) {
    throw new Error(
      `${jet.trajectoires.length} trajectoire(s) pour ${indices.length} dé(s) relancé(s)`);
  }
  const parDe = new Map<number, { face: Face; pose: DePose }>();
  indices.forEach((indexDuDe, rang) => {
    const trajectoire = jet.trajectoires[rang]!;
    const derniere = (trajectoire.nombreImages - 1) * 7;
    const i = trajectoire.images;
    parDe.set(indexDuDe, {
      face: trajectoire.face,
      pose: {
        position: [i[derniere]!, i[derniere + 1]!, i[derniere + 2]!],
        // L'orientation compte autant que la position : sans elle, un dé gardé
        // servirait de collider de travers et ses voisins rebondiraient faux.
        orientation: [i[derniere + 3]!, i[derniere + 4]!, i[derniere + 5]!, i[derniere + 6]!],
      },
    });
  });
  return parDe;
}

/**
 * Les poses après un jet — sans les valeurs.
 *
 * C'est la forme dont le **mode partie** a besoin : là, la valeur et la garde de
 * chaque dé appartiennent au moteur de règles, et les redoubler ici donnerait deux
 * vérités qui finiraient par diverger. Seules les poses n'existent que dans la scène.
 */
export function posesApresJet(
  poses: readonly DePose[], indices: readonly number[], jet: Jet,
): readonly DePose[] {
  const parDe = posesDuJet(indices, jet);
  return poses.map((pose, index) => parDe.get(index)?.pose ?? pose);
}

/**
 * Applique un jet au tapis du bac à sable : chaque trajectoire va au dé qui a été
 * relancé, avec sa valeur.
 */
export function appliquerJet(
  tapis: EtatTapis, indices: readonly number[], jet: Jet,
): EtatTapis {
  const parDe = posesDuJet(indices, jet);
  return tapis.map((de, index) => {
    const nouveau = parDe.get(index);
    return nouveau ? { ...de, face: nouveau.face, pose: nouveau.pose } : de;
  }) as unknown as EtatTapis;
}

/** Valeurs des trois dés, ou `null` si l'un n'a pas encore été lancé. */
export function mainDuTapis(tapis: EtatTapis): readonly [Face, Face, Face] | null {
  const faces = tapis.map((d) => d.face);
  if (faces.some((f) => f === null)) return null;
  return faces as unknown as readonly [Face, Face, Face];
}
